import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, X, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' or 'yearly'
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: { monthly: 0, yearly: 0 },
      description: 'Perfect for getting started',
      features: [
        { text: 'Unlimited Events', included: true },
        { text: '10 CRM Accounts', included: true },
        { text: '2 Websites (no custom domain)', included: true },
        { text: '2 Digi Presentor/month', included: true },
        { text: '2 Digi Editor images/day (with watermark)', included: true },
        { text: 'Digital Business Card', included: true },
        { text: 'Custom Domain', included: false },
        { text: 'Priority Support', included: false }
      ],
      buttonText: 'Current Plan',
      highlighted: false
    },
    {
      id: 'pro_lite',
      name: 'Pro Lite',
      price: { monthly: 10, yearly: 100 },
      description: 'For growing businesses',
      features: [
        { text: 'Unlimited Events', included: true },
        { text: 'Unlimited CRM Accounts', included: true },
        { text: '10 Websites + Custom Domains', included: true },
        { text: 'Unlimited Digi Presentor', included: true },
        { text: '20 Digi Editor images/day (no watermark)', included: true },
        { text: 'Media Events (Photo/Video Sharing)', included: true },
        { text: 'Community Videos', included: true },
        { text: 'Email Support', included: true },
        { text: 'Advanced Analytics', included: false }
      ],
      buttonText: 'Upgrade to Pro Lite',
      highlighted: true,
      badge: 'Popular'
    },
    {
      id: 'pro_max',
      name: 'Pro Max',
      price: { monthly: 20, yearly: 200 },
      description: 'For power users',
      features: [
        { text: 'Everything in Pro Lite', included: true },
        { text: 'Unlimited Websites', included: true },
        { text: 'Unlimited Digi Editor (no watermark)', included: true },
        { text: 'Priority Support', included: true },
        { text: 'Advanced Analytics', included: true },
        { text: 'White Label Options', included: true },
        { text: 'API Access', included: true },
        { text: 'Custom Integrations', included: true }
      ],
      buttonText: 'Upgrade to Pro Max',
      highlighted: false
    }
  ];

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }

    setLoading(planId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        {
          planId: planId,
          interval: billingInterval
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const yearlyDiscount = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || plan.price.monthly === 0) return null;
    const monthlyTotal = plan.price.monthly * 12;
    const savings = monthlyTotal - plan.price.yearly;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return { savings, percentage };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Unlock powerful features to grow your business
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white p-2 rounded-full shadow-lg">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                billingInterval === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors relative ${
                billingInterval === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => {
            const discount = yearlyDiscount(plan.id);
            return (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.highlighted
                    ? 'ring-4 ring-blue-500 transform scale-105 shadow-2xl'
                    : 'shadow-lg hover:shadow-xl transition-shadow'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <CardHeader className="text-center pb-8 pt-8">
                  <CardTitle className="text-2xl font-bold mb-2">
                    {plan.name}
                  </CardTitle>
                  <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-5xl font-bold text-gray-900">
                        ${plan.price[billingInterval]}
                      </span>
                      {plan.price.monthly > 0 && (
                        <span className="text-gray-600">
                          /{billingInterval === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      )}
                    </div>
                    {billingInterval === 'yearly' && discount && (
                      <p className="text-sm text-green-600 mt-2">
                        Save ${discount.savings}/year ({discount.percentage}% off)
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id || plan.id === 'free'}
                    className={`w-full py-6 text-lg font-semibold ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                        : plan.id === 'free'
                        ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    {loading === plan.id ? 'Loading...' : plan.buttonText}
                  </Button>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={
                            feature.included
                              ? 'text-gray-700'
                              : 'text-gray-400 line-through'
                          }
                        >
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-lg mb-2">Can I change plans later?</h3>
              <p className="text-gray-600">Yes! You can upgrade or downgrade your plan at any time from your dashboard.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">What payment methods do you accept?</h3>
              <p className="text-gray-600">We accept all major credit cards through Stripe, our secure payment processor.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">Can I cancel my subscription?</h3>
              <p className="text-gray-600">Yes, you can cancel anytime. You'll continue to have access until the end of your billing period.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">Do you offer refunds?</h3>
              <p className="text-gray-600">We offer a 30-day money-back guarantee on all paid plans.</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Button onClick={() => navigate('/')} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
